import { useGeoLocation } from '#imports';
import errors from '~/resources/error.parser.json';

const getErrorDetail = (code: string) => (errors as any[]).find(k => k.code === code) || null;

const computeBreakup = (data: Array<any>, groupKey: string, labelKey: string, valueKey: string) =>
  Object.values(
    data.reduce((acc, obj) => {
      const key = obj[groupKey] as string;
      acc[key] ??= { label: obj[labelKey], value: 0 };
      acc[key].value += +(obj[valueKey] ?? 0);
      return acc;
    }, {})
  );

const getProducts = (items: Array<any>, products: Array<any>) => {
  return items
  .map(item => {
    const product = products.find(k => k.item.id === item.item_id);
    return useProductFormat(item, product || null);
  });
}

const getCharges = (summary: Array<any>) => {
  const taxArray = summary.filter(k => ['tax'].includes(k.type.toLowerCase()) && Number(k.total) !== 0);
  const taxBreakup = computeBreakup(taxArray, 'type', 'title', 'total');
  const taxTotal = (taxBreakup as { value: number }[]).reduce((acc, obj) => acc + obj.value, 0);

  const deliveryArray = summary.filter(k => ['delivery'].includes(k.type.toLowerCase()) && Number(k.total) !== 0);
  const deliveryBreakup = computeBreakup(deliveryArray, 'type', 'title', 'total');
  const deliveryTotal = (deliveryBreakup as { value: number }[]).reduce((acc, obj) => acc + obj.value, 0);

  const chargesArray = summary.filter(k => !['item', 'tax', 'delivery', 'discount'].includes(k.type.toLowerCase()) && Number(k.total) !== 0);
  const chargesBreakup = computeBreakup(chargesArray, 'type', 'title', 'total');
  const chargesTotal = (chargesBreakup as { value: number }[]).reduce((acc, obj) => acc + obj.value, 0);

  const discountArray = summary.filter(k => ['discount'].includes(k.type.toLowerCase()) && Number(k.total) !== 0);
  const discountBreakup = computeBreakup(discountArray, 'type', 'title', 'total');
  const discountTotal = (discountBreakup as { value: number }[]).reduce((acc, obj) => acc + obj.value, 0);
  
  return {
    total: deliveryTotal + chargesTotal - Math.abs(discountTotal),
    breakup: [
      ...(taxTotal > 0 ? [{ label: 'Tax', value: taxTotal, currency: true }] : []),
      ...(deliveryTotal > 0? [{ label: 'Delivery charges', value: deliveryTotal, currency: true }]: []),
      ...chargesBreakup.filter(k => Number(k.value) !== 0).map(k => ({ ...k, currency: true })),
      ...discountBreakup.filter(k => Number(k.value) !== 0).map(k => ({label: `Discount (${k.label})`,value: k.value,currency: true }))
    ],
  }
};

const getFulfillment = (fulfillments: Array<any>) => {
  if (fulfillments.length === 0) return null;
  const firstItem = fulfillments[0];

  const category = firstItem['@ondc/org/category']?.split(' ').join('_').toLowerCase() || '';
  const provider = firstItem['@ondc/org/provider_name'] || '';
  const delivery_in_minutes = useDayjs().duration(firstItem['@ondc/org/TAT']).asMinutes();
  const is_serviceable = firstItem.state?.descriptor?.code.toLowerCase() === 'serviceable';
  
  return { category, provider, delivery_in_minutes, is_serviceable };
}

const buildError = (error: any, provider: any, items: any[]) => {
  const detail = error?.code ? getErrorDetail(error.code) : null;
  if (!detail) return null;

  let updatedItems = items;
  if (error.code === '40002') {
    try {
      const itemIds = JSON.parse(error.message)?.map((k: any) => k.item_id) || [];
      updatedItems = items.filter(k => itemIds.includes(k.item_id));
    } catch (e) {
      console.error('Error parsing out-of-stock items:', e);
    }
  }
  return { ...detail, provider, items: updatedItems };
};

const getCartItems = (items: Array<any>, responseData: Array<any>) => {
  const { deliveryAddress } = useGeoLocation();
  const result: Array<any> = [];
  const providers = [...new Set(items.map(k => k.provider.ukid))];

  providers.forEach(ukid => {
    const providerItems = items.filter(k => k.provider.ukid === ukid);
    const { provider, seller, location } = providerItems[0];
    const { id: providerId, name: providerName, images, provider_src } = provider;
    const providerImage = images?.thumbnail || null;
    const { products = [], summary = [], fulfillments = [], error = null } = responseData?.find(k => k.provider.id === providerId) || {};

    const cartItems = getProducts(providerItems, products);
    const charges = getCharges(summary);
    const fulfillmentData = getFulfillment(fulfillments);

    const totalItems = cartItems.length;
    const totalQty = (cartItems as { qty: number }[]).reduce((acc, obj) => acc + obj.qty, 0);
    const totalAmount = (cartItems as { total: number }[]).reduce((acc, obj) => acc + obj.total, 0);

    const isAcceptingOrder = responseData?.some(k => k.provider.id === providerId) ?? false;
    const isServiceable = fulfillmentData?.is_serviceable ?? false;

    const providerInfo = { id: providerId, name: providerName, image: providerImage, location_id: location.id, provider_src };
    const providerError = { ukid, ...providerInfo, totalItems, totalQty, totalAmount };
    
    result.push({
      ukid,
      provider: providerInfo,
      seller_np: { id: seller.id },
      name: providerName,
      products: cartItems,
      charges,
      fulfillment: fulfillmentData,
      totalItems,
      totalQty,
      totalAmount: totalAmount + charges.total,
      error: deliveryAddress && deliveryAddress.gps ? (() => {
        if (!isAcceptingOrder) return { ...getErrorDetail('30001'), provider: providerError, items: cartItems };
        if (!isServiceable) return { ...getErrorDetail('30017'), provider: providerError, items: cartItems };
        return buildError(error, providerError, cartItems);
      })() : null,
    });
  });
  return result;
};

const getBillDetails = (responseData: Array<any>, coupon: any) => {
  const summaryArray = responseData.flatMap(item => item.summary ?? []);
  const itemArray = summaryArray.filter(k => ['item'].includes(k.type.toLowerCase()));
  const taxArray = summaryArray.filter(k => ['tax'].includes(k.type.toLowerCase()) && Number(k.total) !== 0);
  const deliveryArray = summaryArray.filter(k => ['delivery'].includes(k.type.toLowerCase()) && Number(k.total) !== 0);
  const discountArray = summaryArray.filter(k => ['discount'].includes(k.type.toLowerCase()) && Number(k.total) !== 0);
  const miscArray = summaryArray.filter(k => ['misc'].includes(k.type.toLowerCase()) && Number(k.total) !== 0);
  const remainingCharges = ['item', 'tax', 'delivery', 'discount', 'misc'];
  const otherArray = summaryArray.filter(k => !remainingCharges.includes(k.type?.toLowerCase()) && Number(k.total) !== 0);


  const itemTotal = itemArray.reduce((acc, obj) => acc + (obj.total || 0), 0);
  const taxTotal = taxArray.reduce((acc, obj) => acc + (obj.total || 0), 0);
  const deliveryTotal = deliveryArray.reduce((acc, obj) => acc + (obj.total || 0), 0);
  const groupedDiscounts = Object.values(
    discountArray.reduce((acc, curr) => {
      const key = curr.title?.trim() || 'Discount';
      if (!acc[key]) acc[key] = { title: key, total: 0 };
      acc[key].total += curr.total || 0;
      return acc;
    }, {} as Record<string, { title: string; total: number }>)
  );
  
  const discountTotal = groupedDiscounts.reduce((acc, obj) => acc + (obj.total || 0), 0);
  const miscTotal = miscArray.reduce((acc, obj) => acc + (obj.total || 0), 0);

  const couponAmount = coupon.is_applied ? +(coupon.detail.offer_amount || 0) : 0;
  const amountToPay = itemTotal + taxTotal + deliveryTotal + discountTotal - couponAmount + miscTotal + otherArray.reduce((acc, obj) => acc + (obj.total || 0), 0);

  const labelClass = 'text-xs text-gray-600';
  const valueClass = 'text-xs text-gray-900';
  const footerClass = 'font-medium text-black text-sm';
  const valueGreen = `${valueClass} !text-green-500`;

  return [
    { id: 'item', label: `Item total (${itemArray.length})`, value: itemTotal, currency: true, labelClass, valueClass },
    ...(taxTotal > 0 ? [{ id: 'tax', label: 'Tax', value: taxTotal, currency: true, labelClass, valueClass }] : []),
    ...miscArray.filter(k => +k.total !== 0).map(k => ({ label: k.title, value: k.total, currency: true, labelClass, valueClass })),
    ...groupedDiscounts.filter(k => +k.total !== 0).map(k => ({label: `Discount (${k.title})`,value: Math.abs(k.total), currency: true, labelClass, valueClass: valueGreen, prefix: '-' })),
    ...(coupon.is_applied ? [{ label: `Coupon (${coupon.code})`, value: Math.abs(couponAmount), currency: true, labelClass, valueClass: valueGreen, prefix: '-' }] : []),
    ...(deliveryTotal > 0
      ? [{ id: 'delivery', label: 'Delivery charges', value: deliveryTotal, currency: true, labelClass, valueClass }]
      : [{ label: 'Delivery charges', value: 'Free', labelClass, valueClass: valueGreen }]
    ),
    ...otherArray.map(k => ({label: k.title || k.type, value: k.total, currency: true, labelClass,valueClass: valueGreen})), 
    { label: 'Total payable amount', value: amountToPay, currency: true, labelClass: footerClass, valueClass: footerClass, footer: true },
  ]; 
};

const getMetrics = (items: Array<any>) => {
  const skuIds = [...new Set(items.map(k => k.sku_id))];
  const ukIds = [...new Set(items.map(k => k.provider.ukid))];
  const sellerIds = [...new Set(items.map(k => k.seller?.id))];
  const categoryIds = [...new Set(items.map((item) => item.category?.parent?.name))];
  const subCategoryIds = [...new Set(items.map((item) => item.category?.name))];
  const provider_src = [...new Set(items.map(k => k.provider?.provider_src))];
  const providerIds = [...new Set(items.map(k => k.provider?.id))];
  const productIds = [...new Set(items.map(item => item.sku_id))];
  const parentItemIds = [...new Set(items.map(item => item.parent_item_id))];

  return { skuIds, ukIds, sellerIds, categoryIds, subCategoryIds, providerIds, productIds, provider_src, parentItemIds };
}

export const useCartSummary = () => {
  const cart = useCart();
  const geolocation = useGeoLocation();
  const transactionId = computed(() => cart.transaction_id);
  const responseData = computed(() => cart.response?.data || []);
  const cartItems = computed(() => getCartItems(cart.items, responseData.value));
  const metrics = computed(() => getMetrics(cart.items));
  const coupon = computed(() => ({ 
    is_applied: cart.offers.offer_code ? true : false,
    code: cart.offers.offer_code,
    detail: cart.offers,
  }));
  const billDetails = computed(() => getBillDetails(responseData.value, coupon.value));
  const amountToPay = computed(() => billDetails.value[billDetails.value.length - 1].value || 0);
  const hasError = computed(() => geolocation.isValidDeliveryAddress && !isLoading.value && cartItems.value.some((k: any) => k.error));
  const isLoading = computed(() => cart.loader);
  const isDirty = ref(false);

  onMounted(() => {
    watch(cart.items, async () => { isDirty.value = true, { deep: true } });
    watch(responseData, (_newValue, oldValue) => {
      if (oldValue.length === 0) isDirty.value = false;
    });
  });
  return {
    transactionId,
    cartItems,
    coupon, 
    billDetails,
    amountToPay,
    metrics,
    hasError,
    isLoading,
    isDirty
  };
}
